// Libraries
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";

//MUI
import { Box, Link, Stack, Typography } from "@mui/material";
import { Person, AccessTime, CalendarToday } from "@mui/icons-material";

//COMPONENTS
import {
  bookAppointment,
  createDynamoAppointment,
  getBookedAppointments,
  getSuggestions,
  sendConfirmation,
} from "api/AppointmentEndpointRequests";
import {
  fetchWaitlistByPatientID,
  scheduleWaitlist,
  sendWaitlistMessage,
} from "api/WaitlistEndpointRequests";
import BookingForm from "./BookingForm";
import { Loading } from "../Common";
import { Slots } from "./Slots";
import { combineDateAndTime } from "components/Common/FormatTime";
import handleCancelWaitlist from "components/Waitlist/handleCancelWaitlist";
import ConfirmModal from "../Common/ConfirmModal";
import FeedbackModal from "../Common/FeedbackModal";
import { useBookingFormik } from "./BookingFormik";

// Redux
import { useSelector } from "react-redux";
import { selectProviders, selectProvidersLoading } from "Store/ProvidersSlice";

//Constants
import CustomButton from "constants/CustomButton";
import { styles } from "./styles";
import { styles as mainStyles } from "../styles";

const Booking = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [highlightedDays, setHighlightedDays] = useState([]);
  const [suggestionNames, setSuggestionNames] = useState([]);
  const [bookedAppointmentTimes, setBookedAppointmentTimes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOnWaitlist, setIsOnWaitlist] = useState(false);
  const [waitlistData, setWaitlistData] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);

  const doctors = useSelector(selectProviders);
  const doctorsLoading = useSelector(selectProvidersLoading);

  const adult =
    Math.floor(
      (new Date() - new Date(state.demographics.birthday).getTime()) /
        3.15576e10
    ) >= 18;
  const today = moment(new Date()).format("YYYY-MM-DD");
  const tomorrow = moment(today).add(1, "day").format("YYYY-MM-DD");

  const hasCellPhone = state.demographics.phones.some(
    (item) => item.contactType === "CellPhone"
  );
  const cellPhone = hasCellPhone
    ? `+1${state.demographics.phones
        .filter((item) => item.contactType === "CellPhone")[0]
        ?.["number"].replace(/\D/g, "")}`
    : "";

  const patientName = `${state.demographics.firstName} ${state.demographics.lastName}`;

  useEffect(() => {
    const getBookings = async () => {
      setLoading(true);
      await getBookingInformation();
      await checkPatientWaitlistStatus(state.patientId);
      setLoading(false);
    };
    getBookings();
  }, [doctorsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const getBookingInformation = async () => {
    const bookedAppointments = await getBookedAppointments(today, tomorrow);
    const providersReturn = doctors.allProviders;

    const pediatricianIds = providersReturn
      .filter((doc) => doc.typeId === 28)
      .map((doc) => doc.id);
    setProviders(providersReturn);
    setBookedAppointmentTimes(bookedAppointments);

    if (providersReturn.length) {
      const suggestions = await getSuggestions(today, tomorrow);
      const pediatricianSuggestions = suggestions.filter((s) =>
        pediatricianIds.includes(s.providerId)
      );
      const adultSuggestions = suggestions.filter(
        (s) => !pediatricianIds.includes(s.providerId)
      );
      const filteredSuggestions = adult
        ? adultSuggestions
        : pediatricianSuggestions;

      const allSlots = Slots("", "", bookedAppointments, filteredSuggestions);
      fetchHighlightedDays(allSlots);

      setSuggestionNames([...new Set(filteredSuggestions.map((s) => s.name))]);
      setFilteredSuggestions(filteredSuggestions);
    }
  };
  const checkPatientWaitlistStatus = async (patientId) => {
    const data = await fetchWaitlistByPatientID(patientId);
    if (data.contents && data.contents.length > 0) {
      const activeWaitlist = data.contents.filter(
        (waitlist) => waitlist.caseState === "Active"
      );
      if (activeWaitlist.length > 0) {
        setWaitlistData(activeWaitlist);
        setIsOnWaitlist(true);
      }
    }
  };

  const handleJoinWaitlist = async (values) => {
    try {
      const body = {
        waitlistProviderId: process.env.REACT_APP_ENV_WAITLIST_PROVIDER_ID,
        patientId: state.patientId,
        consultPriority: process.env.REACT_APP_ENV_CONSULT_PRIORITY,
        consultStatus: "Waiting",
        consultType: "Inpatient",
        referralDate: values.date,
        requestDate: values.date,
        notes: `Booked on ${moment().format(
          "YYYY-MM-DD @ HH:mm"
        )} \nPatient Notes: ${values.notes}`,
      };
      const message = {
        name: state.demographics.firstName,
        date: moment(values.date).format("dddd, MMMM Do YYYY"),
        cell: cellPhone,
        reason: values.reason,
      };
      const data = await scheduleWaitlist(body);
      if (
        !data.error &&
        data.message !== "Internal server error" &&
        hasCellPhone
      ) {
        await sendWaitlistMessage(message);
        return { success: true };
      } else {
        return { success: false, message: data.error ?? data.message };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const handleBookAppointment = async (values) => {
    try {
      let start = moment(values.available.time, "hh:mm a")
        .format("Hmm")
        .toString();
      let end = moment(start, "Hmm").add(15, "minutes");
      end = moment(end, "H").format("Hmm");
      let body = {
        date: values.date,
        startTime: start,
        endTime: end,
        subColumn: values.available.subColumn,
        patientId: state.patientId,
        providerId: values.available.id,
        resourceId: null,
        officeId: 9392,
        appointmentDetails: {
          billingProviderId: values.available.id,
          referringProviderId: null,
          notes: values.notes,
          reasonId: null,
          typeId: null,
          priorityId: null,
          arrived: false,
          confirmed: false,
          serviceLocation: "O",
        },
      };
      const combinedDateTime = combineDateAndTime(values.date, start);
      let reminderDateTime = moment(combinedDateTime).subtract(150, "minutes");
      let dateTS =
        reminderDateTime > moment().add(15, "minutes")
          ? reminderDateTime
          : null;
      const message = {
        name: state.demographics.firstName,
        date: moment(values.date).format("dddd, MMMM Do YYYY"),
        startTime: values.available.time,
        reminderAt: dateTS,
        provider: providers.find(
          (provider) => provider.id === values.available.id
        ).fullName,
        cell: cellPhone,
        reason: values.reason,
      };
      if (waitlistData.length) {
        await handleCancelWaitlist(
          state.patientId,
          waitlistData[0].id,
          waitlistData[0].referralDate,
          waitlistData[0].requestDate,
          values.date
        );
      }
      const data = await bookAppointment(body);
      if (!data.error && !data.message) {
        let messageResponse = null;
        if (hasCellPhone) {
          messageResponse = await sendConfirmation(message);
        }
        const appointment = {
          appointmentId: data,
          patientId: state.patientId,
          date: values.date,
          time: values.available.time,
          phoneNumber: cellPhone,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          confirmationTwilioId: messageResponse?.confirmationSid,
          scheduledTwilioId: messageResponse?.scheduledSid,
          status: "CREATED",
          apptSuggestion: values.reason,
        };
        await createDynamoAppointment(appointment);
        return { success: true };
      } else {
        formik.setFieldValue("available", "");
        formik.setFieldError("available", undefined);
        formik.setFieldTouched("available", false);
        await getBookingInformation();
        return { success: false, message: data.error ?? data.message };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const handleConfirmBooking = async (values) => {
    setLoadingAction(true);
    const response = await handleBookAppointment(values);
    if (response.success) {
      setFeedbackData({
        success: true,
        title: "Appointment Successfully Booked!",
      });
    } else {
      setFeedbackData({
        success: false,
        title: "Something went wrong",
        message: `${response.message} Please check your details and try again.`,
      });
    }
    handleCloseConfirm();
    setLoadingAction(false);
    setOpenFeedback(true);
  };

  const handleConfirmWaitlist = async (values) => {
    setLoadingAction(true);
    const response = await handleJoinWaitlist(values);
    if (response.success) {
      setFeedbackData({
        success: true,
        title: "Successfully Joined Waitlist!",
      });
    } else {
      setFeedbackData({
        success: false,
        title: "Something went wrong",
        message: `${response.message} Please check your details and try again.`,
      });
    }
    handleCloseConfirm();
    setLoadingAction(false);
    setOpenFeedback(true);
  };

  const getBookConfirmData = (values) => ({
    title: "CONFIRM BOOKING",
    message: (
      <Stack>
        <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
          <Stack direction="row">
            <Person sx={mainStyles.icon} />
            <Typography>{patientName}</Typography>
          </Stack>
          <Stack direction="row">
            <CalendarToday sx={mainStyles.icon} />
            <Typography>
              {DateTime.fromISO(values.date).toFormat("cccc, LLLL dd yyyy")}
            </Typography>
          </Stack>
          <Stack direction="row">
            <AccessTime sx={mainStyles.icon} />
            <Typography>{values.available.time}</Typography>
          </Stack>
        </Stack>
        <Typography sx={mainStyles.leftAlignedTypographyBody}>
          If you are unable to attend, please cancel your appointment at least 2
          hours in advance to avoid late cancellation fees.
        </Typography>
      </Stack>
    ),
    onConfirm: () => {
      handleConfirmBooking(values);
    },
    confirmText: "Book Appointment",
  });

  const getWaitlistConfirmData = (values) => ({
    title: "CONFIRM WAITLIST",
    message: (
      <Stack>
        <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
          <Stack direction="row">
            <Person sx={mainStyles.icon} />
            <Typography>{patientName}</Typography>
          </Stack>
          <Stack direction="row">
            <CalendarToday sx={mainStyles.icon} />
            <Typography>
              {DateTime.fromISO(values.date).toFormat("cccc, LLLL dd yyyy")}
            </Typography>
          </Stack>
        </Stack>

        <Typography sx={mainStyles.leftAlignedTypographyBody}>
          If an appointment becomes available, you will receive a call and can
          choose whether to book it.
        </Typography>
      </Stack>
    ),
    onConfirm: () => {
      handleConfirmWaitlist(values);
    },
    confirmText: "Join Waitlist",
  });

  const handleOpenConfirm = (values) => {
    const data = values.joinWaitlist
      ? getWaitlistConfirmData(values)
      : getBookConfirmData(values);

    setConfirmData(data);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setConfirmData(null);
  };

  const handleCloseFeedback = () => {
    if (feedbackData.success) {
      navigate("/");
    }
    setOpenFeedback(false);
    setFeedbackData(null);
  };

  const formik = useBookingFormik(handleOpenConfirm);

  /**
   * Gets the appointment time slots.
   *
   * @param {Array} suggestions Appointment suggestions
   * @return {Array} Appointment session time slots
   */
  const slots = useMemo(() => {
    return Slots(
      formik.values.date,
      formik.values.reason,
      bookedAppointmentTimes,
      filteredSuggestions
    );
  }, [
    formik.values.date,
    formik.values.reason,
    bookedAppointmentTimes,
    filteredSuggestions,
  ]);

  /**
   * Sets the highlighted days state based on all available slots.
   *
   * @param {number} slots All available slots
   */
  const fetchHighlightedDays = (slots) => {
    if (slots.length === 0) return;
    const days = slots.map((slot) => {
      return {
        day: parseInt(slot.date.substring(8, 10)),
        month: parseInt(slot.date.substring(5, 7)),
      };
    });
    setHighlightedDays(days);
  };

  return (
    <Box sx={styles.backgroundBox}>
      {(loading || doctorsLoading) && <Loading />}
      {filteredSuggestions.length === 0 && !loading && !doctorsLoading && (
        <>
          <Typography sx={mainStyles.typographyTitle}>
            There are no available booking slots.
          </Typography>
          <Typography
            sx={{
              ...mainStyles.leftAlignedTypographyBody,
              textAlign: "center",
            }}
          >
            There are no providers currently available so please contact the
            clinic directly at{" "}
            <Link href="tel:+14312222273" color="inherit">
              (431) 222 - 2273
            </Link>{" "}
            to book.
          </Typography>
          <CustomButton
            label="Back"
            type="secondary"
            sx={{ mt: 2 }}
            onClick={() => navigate("/")}
          />
        </>
      )}
      {filteredSuggestions.length > 0 && !loading && !doctorsLoading && (
        <BookingForm
          formik={formik}
          activeWaitlist={isOnWaitlist}
          suggestionNames={suggestionNames}
          highlightedDays={highlightedDays}
          slots={slots}
          refreshAppointments={getBookingInformation}
        />
      )}
      {openConfirm && (
        <ConfirmModal
          open={openConfirm}
          title={confirmData.title}
          message={confirmData.message}
          onConfirm={confirmData.onConfirm}
          confirmText={confirmData.confirmText}
          loadingAction={loadingAction}
          onCancel={handleCloseConfirm}
        />
      )}
      {openFeedback && (
        <FeedbackModal
          open={openFeedback}
          success={feedbackData.success}
          title={feedbackData.title}
          message={feedbackData.message ?? ""}
          onConfirm={handleCloseFeedback}
        />
      )}
    </Box>
  );
};

export default Booking;
